import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Globe, BookOpen, Loader2, FileText, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { API } from "../config";

// Worker pdf.js servito localmente dal frontend. Vedi PublicManualViewerPage
// per il razionale CSP + il fix MIME-type del 11/06/2026.
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const LANGUAGES = [
  { code: "EN", name: "English", flag: "\u{1F1EC}\u{1F1E7}" },
  { code: "IT", name: "Italiano", flag: "\u{1F1EE}\u{1F1F9}" },
  { code: "FR", name: "Francais", flag: "\u{1F1EB}\u{1F1F7}" },
  { code: "ES", name: "Espanol", flag: "\u{1F1EA}\u{1F1F8}" },
  { code: "DE", name: "Deutsch", flag: "\u{1F1E9}\u{1F1EA}" },
  { code: "PT", name: "Portugues", flag: "\u{1F1F5}\u{1F1F9}" },
  { code: "NL", name: "Nederlands", flag: "\u{1F1F3}\u{1F1F1}" },
  { code: "PL", name: "Polski", flag: "\u{1F1F5}\u{1F1F1}" },
  { code: "SE", name: "Svenska", flag: "\u{1F1F8}\u{1F1EA}" },
  { code: "NO", name: "Norsk", flag: "\u{1F1F3}\u{1F1F4}" },
  { code: "FI", name: "Suomi", flag: "\u{1F1EB}\u{1F1EE}" },
  { code: "DK", name: "Dansk", flag: "\u{1F1E9}\u{1F1F0}" },
  { code: "CZ", name: "Cesky", flag: "\u{1F1E8}\u{1F1FF}" },
  { code: "GR", name: "Ellinika", flag: "\u{1F1EC}\u{1F1F7}" },
];

const ManualViewerPage = () => {
  const { manualId } = useParams();
  const navigate = useNavigate();
  const [manual, setManual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState("EN");
  const [translating, setTranslating] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [viewMode, setViewMode] = useState("pdf");

  // PDF viewer state
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.2);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(false);

  const pdfProxyUrl = `${API}/manuals/${manualId}/pdf-proxy`;

  const fetchManual = async (language) => {
    try {
      if (language === "EN") {
        const res = await axios.get(`${API}/manuals/${manualId}`);
        setManual(res.data);
      } else {
        setTranslating(true);
        const res = await axios.get(`${API}/manuals/${manualId}/translate/${language}`);
        setManual(prev => ({
          ...prev,
          title: res.data.title,
          description: res.data.description,
          sections: res.data.sections,
        }));
      }
    } catch (err) {
      toast.error("Errore caricamento manuale");
    } finally {
      setLoading(false);
      setTranslating(false);
    }
  };

  useEffect(() => { fetchManual("EN"); }, [manualId]);

  const handleLangChange = (newLang) => {
    setLang(newLang);
    if (newLang !== "EN") {
      setViewMode("text");
    }
    fetchManual(newLang);
  };

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setPdfLoading(false);
    setPdfError(false);
  }, []);

  const onDocumentLoadError = useCallback(() => {
    setPdfLoading(false);
    setPdfError(true);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-lojack-black flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!manual) {
    return (
      <div className="min-h-screen bg-lojack-black flex items-center justify-center">
        <p className="text-gray-500">Manuale non trovato</p>
      </div>
    );
  }

  const sections = manual.sections || [];
  const hasPdf = !!manual.pdf_url;

  return (
    <div className="min-h-screen bg-lojack-black" data-testid="manual-viewer-page">
      {/* Sticky Header */}
      <div className="sticky top-0 z-50 bg-[#0D0D0D]/95 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors flex-shrink-0"
                data-testid="manual-back-btn"
              >
                <ArrowLeft size={18} className="text-gray-400" />
              </button>
              <h1 className="text-lg font-bold text-white truncate" data-testid="manual-title">
                {manual.title}
              </h1>
              {translating && <Loader2 className="w-5 h-5 text-lojack-red animate-spin flex-shrink-0" />}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* View Mode Toggle */}
              {hasPdf && (
                <div className="flex bg-[#1A1A1A] border border-gray-700 rounded-lg overflow-hidden">
                  <button
                    onClick={() => { setViewMode("pdf"); setLang("EN"); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${viewMode === "pdf" ? "bg-lojack-red text-white" : "text-gray-400 hover:text-white"}`}
                    data-testid="view-mode-pdf"
                  >
                    <BookOpen size={14} /> PDF
                  </button>
                  <button
                    onClick={() => setViewMode("text")}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors ${viewMode === "text" ? "bg-lojack-red text-white" : "text-gray-400 hover:text-white"}`}
                    data-testid="view-mode-text"
                  >
                    <FileText size={14} /> Testo
                  </button>
                </div>
              )}

              {/* Language Selector - only for text mode */}
              {viewMode === "text" && (
                <div className="relative">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1A1A1A] border border-gray-700 rounded-lg">
                    <Globe size={14} className="text-lojack-red" />
                    <select
                      value={lang}
                      onChange={(e) => handleLangChange(e.target.value)}
                      disabled={translating}
                      className="bg-transparent text-white text-sm outline-none cursor-pointer pr-4 appearance-none"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%23E31837' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 0 center",
                      }}
                      data-testid="manual-lang-selector"
                    >
                      {LANGUAGES.map((l) => (
                        <option key={l.code} value={l.code} style={{ backgroundColor: "#1A1A1A", color: "#fff" }}>
                          {l.flag} {l.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* PDF Download */}
              {manual.pdf_url && (
                <a
                  href={manual.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-lojack-red/10 border border-lojack-red/30 rounded-lg text-lojack-red text-sm font-medium hover:bg-lojack-red/20 transition-colors"
                  data-testid="manual-pdf-download"
                >
                  <Download size={14} />
                  Scarica
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* PDF View */}
      {viewMode === "pdf" && hasPdf && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6" data-testid="pdf-viewer-container">
          {/* PDF Controls */}
          <div className="flex items-center justify-center gap-4 mb-4 bg-[#111] border border-gray-800 rounded-xl p-3">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="pdf-prev-page"
            >
              <ChevronLeft size={18} className="text-white" />
            </button>
            <span className="text-gray-300 text-sm font-mono min-w-[100px] text-center" data-testid="pdf-page-info">
              {numPages ? `${currentPage} / ${numPages}` : "Caricamento..."}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(numPages || 1, p + 1))}
              disabled={currentPage >= (numPages || 1)}
              className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="pdf-next-page"
            >
              <ChevronRight size={18} className="text-white" />
            </button>
            <div className="w-px h-6 bg-gray-700" />
            <button onClick={() => setScale(s => Math.max(0.5, s - 0.2))} className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors" data-testid="pdf-zoom-out">
              <ZoomOut size={18} className="text-white" />
            </button>
            <span className="text-gray-400 text-xs font-mono w-12 text-center">{Math.round(scale * 100)}%</span>
            <button onClick={() => setScale(s => Math.min(3, s + 0.2))} className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors" data-testid="pdf-zoom-in">
              <ZoomIn size={18} className="text-white" />
            </button>
          </div>

          {/* PDF Document */}
          <div className="flex justify-center overflow-auto bg-[#1A1A1A] border border-gray-800 rounded-2xl p-4 min-h-[600px]">
            {pdfError ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <BookOpen size={48} className="text-gray-600" />
                <p className="text-gray-400">Impossibile caricare il PDF.</p>
                <a href={manual.pdf_url} target="_blank" rel="noopener noreferrer" className="btn-lojack text-sm flex items-center gap-2">
                  <Download size={16} /> Apri PDF Originale
                </a>
              </div>
            ) : (
              <Document
                file={pdfProxyUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <div className="flex flex-col items-center justify-center py-20 gap-3">
                    <Loader2 className="w-8 h-8 text-lojack-red animate-spin" />
                    <p className="text-gray-400 text-sm">Caricamento PDF...</p>
                  </div>
                }
              >
                <Page
                  pageNumber={currentPage}
                  scale={scale}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  loading={
                    <div className="flex items-center justify-center py-20">
                      <Loader2 className="w-6 h-6 text-lojack-red animate-spin" />
                    </div>
                  }
                />
              </Document>
            )}
          </div>

          {/* Quick page nav */}
          {numPages && numPages > 1 && (
            <div className="flex items-center justify-center gap-1 mt-4 flex-wrap">
              {Array.from({ length: numPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-8 h-8 rounded-lg text-xs font-mono transition-colors ${
                    page === currentPage
                      ? "bg-lojack-red text-white"
                      : "bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white"
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Text View */}
      {viewMode === "text" && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex gap-8">
            {/* Sidebar TOC - Desktop */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-20">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Indice</h3>
                <nav className="space-y-1">
                  {sections.map((s, i) => (
                    <a
                      key={s.id}
                      href={`#section-${s.id}`}
                      onClick={() => setActiveSection(s.id)}
                      className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                        activeSection === s.id
                          ? "bg-lojack-red/10 text-lojack-red border-l-2 border-lojack-red"
                          : "text-gray-400 hover:text-white hover:bg-gray-800/50"
                      }`}
                    >
                      <span className="text-gray-600 mr-2">{i + 1}.</span>
                      {s.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 min-w-0">
              {/* Description */}
              <div className="bg-[#111] border border-gray-800 rounded-2xl p-6 mb-8">
                <p className="text-gray-400 text-sm" data-testid="manual-description">{manual.description}</p>
                {translating && (
                  <div className="mt-3 flex items-center gap-2 text-lojack-red text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Traduzione AI in corso...
                  </div>
                )}
              </div>

              {/* Sections */}
              <div className="space-y-6">
                {sections.map((section, i) => (
                  <section
                    key={section.id}
                    id={`section-${section.id}`}
                    className="bg-[#111] border border-gray-800 rounded-2xl overflow-hidden scroll-mt-20"
                    data-testid={`manual-section-${section.id}`}
                  >
                    <div className="px-6 py-4 border-b border-gray-800 bg-[#0D0D0D]">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-full bg-lojack-red/10 border border-lojack-red/30 flex items-center justify-center text-lojack-red text-sm font-bold flex-shrink-0">
                          {i + 1}
                        </span>
                        <h2 className="text-lg font-bold text-white">{section.title}</h2>
                      </div>
                    </div>
                    <div className="px-6 py-5">
                      <div className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                        {section.content}
                      </div>
                    </div>
                  </section>
                ))}
              </div>

              {/* Footer */}
              <div className="mt-10 pt-6 border-t border-gray-800 text-center">
                <p className="text-gray-600 text-xs">
                  &copy; {new Date().getFullYear()} LoJack Italia S.r.l. / CalAmp Corp. - Tutti i diritti riservati
                </p>
                {lang !== "EN" && (
                  <p className="text-gray-700 text-xs mt-1">
                    Traduzione automatica AI - Verificare con il documento originale in caso di dubbi
                  </p>
                )}
              </div>
            </main>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualViewerPage;
